import { useState } from "react";
import { useNavigate, Link, Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Loader2, ShieldAlert } from "lucide-react";

export default function Login() {
  const { login, user, config } = useAuth();
  const [params] = useSearchParams();
  const expired = params.get("expired") === "1";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate("/");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex justify-center" style={{ background: "radial-gradient(ellipse at top, #1F2D7A 0%, #0B1437 55%, #050A1C 100%)" }}>
      <div className="w-full md:max-w-[540px] md:my-6 md:rounded-[26px] md:shadow-[0_8px_40px_rgba(0,0,0,0.45)] bg-curtron-bg overflow-hidden">
        <div className="px-6 py-10">
          {/* Brand */}
          <div className="flex flex-col items-center text-center mb-8">
            <img src="/curtron-emblem.png" alt="Curtron" className="h-10 w-auto dark:brightness-0 dark:invert" />
            <div className="font-heading font-bold text-curtron-ink text-xl mt-3">Curtron Hub</div>
            <div className="text-xs text-curtron-muted mt-0.5" data-testid="brand-alignment">a service of Curtron Transport Services</div>
          </div>

          {expired && (
            <div className="flex items-center gap-2 text-sm text-[#8a6414] bg-curtron-ochre/15 border border-curtron-ochre/30 rounded-md px-3 py-2 mb-4" data-testid="session-expired-banner">
              <ShieldAlert className="w-4 h-4 shrink-0" /> Your session has expired. Please sign in again.
            </div>
          )}

          <h1 className="font-heading text-2xl font-bold text-curtron-ink tracking-tight text-center">Sign in</h1>
          <p className="text-sm text-curtron-sub mt-1 mb-6 text-center">Access your dispatch dashboard.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.05em] font-medium text-curtron-sub">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input" autoComplete="email" placeholder="you@curtron.com"
                className="mt-1.5 w-full h-11 px-3 rounded-md border border-curtron-line bg-curtron-surface text-sm outline-none focus:border-curtron-green focus:ring-2 focus:ring-curtron-green/20 transition" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.05em] font-medium text-curtron-sub">Password</label>
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input" autoComplete="current-password" placeholder="••••••••"
                className="mt-1.5 w-full h-11 px-3 rounded-md border border-curtron-line bg-curtron-surface text-sm outline-none focus:border-curtron-green focus:ring-2 focus:ring-curtron-green/20 transition" />
            </div>
            {error && <div data-testid="login-error" className="text-sm text-curtron-terracotta bg-curtron-terracotta/10 rounded-md px-3 py-2">{error}</div>}
            <button type="submit" disabled={loading} data-testid="login-submit-btn"
              className="w-full h-11 rounded-md bg-curtron-green text-white text-sm font-medium hover:bg-curtron-greenHover transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} Sign in
            </button>
          </form>

          {config?.signup_enabled && (
            <p className="text-sm text-curtron-sub mt-6 text-center">
              Need an account? <Link to="/signup" data-testid="goto-signup-link" className="text-curtron-green font-medium hover:underline">Sign up</Link>
            </p>
          )}
          <p className="text-xs text-curtron-muted mt-2 text-center">Contact your administrator if you need access.</p>

          {/* Public compliance disclosures */}
          <div className="mt-10 space-y-5 border-t border-curtron-line pt-6">
            <div>
              <h2 className="font-heading font-semibold text-curtron-ink text-sm">About Curtron Hub</h2>
              <p className="text-xs text-curtron-sub leading-relaxed mt-1.5">
                Curtron Hub is a private mobile &amp; web dispatch application operated by Curtron Transport Services, a licensed transporter of decedent remains serving funeral homes and coroners' offices in Georgia. Access is restricted to authorized staff and contracted drivers.
              </p>
            </div>
            <div>
              <h2 className="font-heading font-semibold text-curtron-ink text-sm">SMS Dispatch Alerts</h2>
              <p className="text-xs text-curtron-sub leading-relaxed mt-1.5">
                Drivers may opt in inside the Curtron Hub app (Profile → "Receive dispatch alerts by text message") to receive one-time operational SMS notifications when a transport call is assigned to them. Each message lists the agency, pickup address, and scheduled time. Message frequency varies (typically 1–5 per week). Message &amp; data rates may apply. Reply STOP to any message to unsubscribe, or HELP for help. Consent is not a condition of employment. Mobile numbers are never sold or shared with third parties for marketing purposes.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
              <Link to="/privacy" data-testid="footer-privacy" className="text-curtron-green font-medium hover:underline">Privacy Policy</Link>
              <Link to="/terms" data-testid="footer-terms" className="text-curtron-green font-medium hover:underline">Terms of Service</Link>
              <Link to="/opt-in" data-testid="footer-optin" className="text-curtron-green font-medium hover:underline">How to receive text alerts</Link>
              <a href="mailto:support@curtronhub.com" data-testid="footer-contact" className="text-curtron-green font-medium hover:underline">Contact</a>
            </div>
            <p className="text-[11px] text-curtron-muted">© 2026 Curtron Transport Services. All rights reserved.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
